// src/components/AGTFit/food/DetailedNutrition.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  IconButton,
  Button,
  useTheme,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Slider,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Apple,
  Plus,
  Scan,
  Trash2,
  Edit2,
  X,
  Check
} from 'lucide-react';
import FoodSearch from './FoodSearch';
import agtfitService from '../service/agtfitService';

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

const MacroCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(3),
  boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 2px 10px rgba(0,0,0,0.05)',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

const FoodItemCard = styled(Box)(({ theme, active = true }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  opacity: active ? 1 : 0.6,
  transition: 'opacity 0.2s ease',
  cursor: 'pointer',
}));

const IconBox = styled(Box)(({ theme, color = FIT_ORANGE }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
  color: color,
}));

// Botón circular con gradiente naranja sin sombra
const AddButton = styled(IconButton)(({ theme }) => ({
  width: 40,
  height: 40,
  background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
  color: '#FFFFFF',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: 'none',
  '&:hover': {
    background: 'linear-gradient(90deg, #FF8800 0%, #FF5B00 100%)',
  }
}));

// Botón para escanear código de barras sin sombra y elevación
const ScanButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  boxShadow: 'none',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(128, 128, 128, 0.35)'
      : (theme.palette.mode === 'dark' ? '#3C3C3E' : '#E5E5E7'),
  },
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  elevation: 0,
}));

// Botón de acción para el modal - Corregido eliminando propiedades personalizadas
const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  boxShadow: 'none',
}));

// Estilo personalizado para los diálogos con bordes redondeados
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    margin: theme.spacing(2),
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bg
      : undefined,
    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
    border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : undefined,
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
  },
  '& .MuiDialogTitle-root': {
    padding: theme.spacing(3, 3, 2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2, 3, 3),
  }
}));

// Función para redondear valores al entero más cercano
const roundNumber = (num) => {
  if (num === undefined || num === null) return 0;
  const decimal = num - Math.floor(num);
  return decimal >= 0.5 ? Math.ceil(num) : Math.floor(num);
};

const DetailedNutrition = ({ todayMeals = [], onMealAdded, onMealUpdated, onMealDeleted, selectedDate }) => {
  const theme = useTheme();
  const [openFoodSearch, setOpenFoodSearch] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState('');
  const [openBarcode, setOpenBarcode] = useState(false);
  const [openFoodModal, setOpenFoodModal] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedFood, setEditedFood] = useState(null);
  const [activeFoods, setActiveFoods] = useState({});

  // Inicializar el estado de activación de alimentos
  useEffect(() => {
    const initialActiveFoods = {};
    todayMeals.forEach(meal => {
      initialActiveFoods[meal.mealId] = true;
    });
    setActiveFoods(initialActiveFoods);
  }, [todayMeals]);

  // Limpiar el timeout al desmontar el componente
  const handleOpenFoodSearch = (mealType) => {
    setSelectedMealType(mealType);
    setOpenFoodSearch(true);
  };

  const handleScanBarcode = () => {
    setOpenBarcode(true);
  };

  const handleMealAdded = (meal) => {
    setOpenFoodSearch(false);
    setOpenBarcode(false);
    if (onMealAdded) {
      onMealAdded(meal);
      // Activar el nuevo alimento automáticamente
      setActiveFoods(prev => ({ ...prev, [meal.mealId]: true }));
    }
  };

  const handleFoodClick = (meal) => {
    setSelectedFood(meal);
    setEditedFood({...meal});
    setOpenFoodModal(true);
    setEditMode(false);
  };

  // Removed unused function: handleToggleActive
  // const handleToggleActive = (mealId) => {
  //   // Cancelar debounce anterior si existe
  //   if (debouncedToggleActive) {
  //     clearTimeout(debouncedToggleActive);
  //   }
  //
  //   // Actualizar estado local inmediatamente para UI responsiva
  //   setActiveFoods(prev => ({ ...prev, [mealId]: !prev[mealId] }));
  //
  //   // Debounce la actualización real al backend
  //   const timeoutId = setTimeout(() => {
  //     if (onMealUpdated) {
  //       const meal = todayMeals.find(m => m.mealId === mealId);
  //       if (meal) {
  //         onMealUpdated({
  //           ...meal,
  //           isActive: !activeFoods[mealId] // Usar el nuevo valor
  //         });
  //       }
  //     }
  //   }, 500); // 500ms de debounce
  //
  //   setDebouncedToggleActive(timeoutId);
  // };

  const handleCloseModal = () => {
    setOpenFoodModal(false);
    setEditMode(false);
    setEditedFood(null);
  };

  const handleEditMode = () => {
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    try {
      // Validar que haya cambios
      if (!editedFood || !editedFood.mealId) {
        return;
      }
      
      // Actualizar en la base de datos
      const response = await agtfitService.updateMeal(editedFood.mealId, editedFood);
      
      if (response.success) {
        // Notificar al componente padre
        if (onMealUpdated) {
          onMealUpdated(response.data);
        }
        
        // Cerrar modal y salir del modo edición
        setOpenFoodModal(false);
        setEditMode(false);
        setEditedFood(null);
      }
    } catch (error) {
      console.error('Error al actualizar comida:', error);
    }
  };

  const handleDeleteMeal = async () => {
    try {
      if (!selectedFood || !selectedFood.mealId) {
        return;
      }
      
      // Eliminar de la base de datos
      const response = await agtfitService.deleteMeal(selectedFood.mealId);
      
      if (response.success) {
        // Notificar al componente padre
        if (onMealDeleted) {
          onMealDeleted(selectedFood.mealId);
        }
        
        // Cerrar modal
        setOpenFoodModal(false);
      }
    } catch (error) {
      console.error('Error al eliminar comida:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedFood(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Función para categorizar las comidas según el tipo o la hora
  const categorizeMealsByTime = (meals) => {
    const categorizedMeals = {
      breakfast: [],
      lunch: [],
      dinner: [],
      snacks: []
    };

    meals.forEach(meal => {
      // Si ya tiene un tipo específico, usarlo
      if (meal.type && ['breakfast', 'lunch', 'dinner', 'snack', 'snacks'].includes(meal.type.toLowerCase())) {
        const type = meal.type.toLowerCase() === 'snack' ? 'snacks' : meal.type.toLowerCase();
        categorizedMeals[type].push(meal);
      } else if (meal.mealType && ['breakfast', 'lunch', 'dinner', 'snack', 'snacks'].includes(meal.mealType.toLowerCase())) {
        const type = meal.mealType.toLowerCase() === 'snack' ? 'snacks' : meal.mealType.toLowerCase();
        categorizedMeals[type].push(meal);
      } else {
        // Categorizar por hora
        const mealTime = new Date(meal.createdAt || Date.now());
        const hour = mealTime.getHours();

        if (hour >= 5 && hour < 11) {
          categorizedMeals.breakfast.push(meal);
        } else if (hour >= 11 && hour < 16) {
          categorizedMeals.lunch.push(meal);
        } else if (hour >= 16 && hour < 22) {
          categorizedMeals.dinner.push(meal);
        } else {
          categorizedMeals.snacks.push(meal);
        }
      }
    });

    return categorizedMeals;
  };

  // Calcular las sumas totales para cada categoría
  const calculateTotals = (meals) => {
    return meals.reduce((acc, meal) => {
      // Solo sumar si el alimento está activo
      if (activeFoods[meal.mealId]) {
        acc.calories += meal.calories || 0;
        acc.protein += meal.protein || 0;
        acc.carbs += meal.carbs || 0;
        acc.fats += meal.fats || 0;
      }
      return acc;
    }, { calories: 0, protein: 0, carbs: 0, fats: 0 });
  };

  // Filtrar comidas por la fecha seleccionada
  const filteredMeals = selectedDate 
    ? todayMeals.filter(meal => {
        // Asegurarse de que meal.mealDate y meal.date estén en el mismo formato
        const mealDate = meal.mealDate || meal.date;
        const formattedSelectedDate = selectedDate instanceof Date 
          ? selectedDate.toISOString().split('T')[0] 
          : selectedDate;
        
        return mealDate === formattedSelectedDate;
      })
    : todayMeals;

  // Categorizar las comidas filtradas
  const categorizedMeals = categorizeMealsByTime(filteredMeals);

  return (
    <MacroCard>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconBox>
            <Apple size={24} />
          </IconBox>
          <Typography variant="h6" fontWeight={600}>
            Detailed Nutrition
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScanButton
            startIcon={<Scan size={16} />}
            disableElevation
            onClick={handleScanBarcode}
          >
            Scan
          </ScanButton>
          <AddButton disableRipple onClick={() => handleOpenFoodSearch('snack')}>
            <Plus size={20} />
          </AddButton>
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        {[
          { type: 'breakfast', label: 'Breakfast', meals: categorizedMeals.breakfast },
          { type: 'lunch', label: 'Lunch', meals: categorizedMeals.lunch },
          { type: 'dinner', label: 'Dinner', meals: categorizedMeals.dinner },
          { type: 'snacks', label: 'Snacks', meals: categorizedMeals.snacks }
        ].map((mealCategory, index) => {
          // Calcular totales para esta categoría
          const totals = calculateTotals(mealCategory.meals);
          
          return (
            <Grid item xs={12} md={6} lg={6} key={index}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  {mealCategory.label}
                </Typography>
                
                {/* Mostrar los totales si hay comidas */}
                {mealCategory.meals.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {roundNumber(totals.calories)} kcal | P: {roundNumber(totals.protein)}g | 
                    C: {roundNumber(totals.carbs)}g | F: {roundNumber(totals.fats)}g
                  </Typography>
                )}
                
                {mealCategory.meals.length > 0 ? (
                  mealCategory.meals.map((meal, idx) => (
                    <FoodItemCard 
                      key={idx} 
                      active={activeFoods[meal.mealId]}
                      onClick={() => handleFoodClick(meal)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {meal.name}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {meal.servingSize || 0} {meal.servingUnit || 'g'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {roundNumber(meal.calories)} kcal
                        </Typography>
                      </Box>
                    </FoodItemCard>
                  ))
                ) : (
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                      textAlign: 'center'
                    }}
                  >
                    <Button
                      startIcon={<Plus size={16} />}
                      sx={{
                        textTransform: 'none',
                        color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : FIT_ORANGE
                      }}
                      size="small"
                      onClick={() => handleOpenFoodSearch(mealCategory.type)}
                    >
                      Add {mealCategory.label}
                    </Button>
                  </Box>
                )}
                
                {/* Siempre mostrar el botón de agregar si hay comidas */}
                {mealCategory.meals.length > 0 && (
                  <Button
                    startIcon={<Plus size={16} />}
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : FIT_ORANGE
                    }}
                    size="small"
                    onClick={() => handleOpenFoodSearch(mealCategory.type)}
                  >
                    Add {mealCategory.label}
                  </Button>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Modal de búsqueda de alimentos */}
      <StyledDialog 
        open={openFoodSearch} 
        onClose={() => setOpenFoodSearch(false)}
        fullWidth
        maxWidth="md"
      >
        <FoodSearch 
          mealType={selectedMealType}
          isModal={true}
          onClose={() => setOpenFoodSearch(false)}
          onSelectMeal={handleMealAdded}
          selectedDate={selectedDate}
        />
      </StyledDialog>

      {/* Modal de escaneo de código de barras */}
      <StyledDialog 
        open={openBarcode} 
        onClose={() => setOpenBarcode(false)}
        fullWidth
        maxWidth="sm"
      >
        <FoodSearch 
          mealType="snack"
          isModal={true}
          onClose={() => setOpenBarcode(false)}
          onSelectMeal={handleMealAdded}
          initialMode="barcode"
          selectedDate={selectedDate}
        />
      </StyledDialog>
      
      {/* Modal de detalles de alimento */}
      <StyledDialog
        open={openFoodModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {editMode ? 'Edit Food' : 'Food Details'}
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedFood && (
            <Box>
              {editMode ? (
                // Modo edición
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Food Name"
                      fullWidth
                      value={editedFood?.name || ''}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Serving Size"
                      type="number"
                      fullWidth
                      value={editedFood?.servingSize || ''}
                      onChange={(e) => handleInputChange('servingSize', parseFloat(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {editedFood?.servingUnit || 'g'}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Serving Unit"
                      fullWidth
                      value={editedFood?.servingUnit || ''}
                      onChange={(e) => handleInputChange('servingUnit', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Calories"
                      type="number"
                      fullWidth
                      value={editedFood?.calories || ''}
                      onChange={(e) => handleInputChange('calories', parseFloat(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            kcal
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Protein"
                      type="number"
                      fullWidth
                      value={editedFood?.protein || ''}
                      onChange={(e) => handleInputChange('protein', parseFloat(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            g
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Carbs"
                      type="number"
                      fullWidth
                      value={editedFood?.carbs || ''}
                      onChange={(e) => handleInputChange('carbs', parseFloat(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            g
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Fats"
                      type="number"
                      fullWidth
                      value={editedFood?.fats || ''}
                      onChange={(e) => handleInputChange('fats', parseFloat(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            g
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              ) : (
                // Modo visualización
                <>
                  <Typography variant="h6" gutterBottom>
                    {selectedFood.name}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body1">
                      {selectedFood.servingSize || 0} {selectedFood.servingUnit || 'g'}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {roundNumber(selectedFood.calories)} kcal
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Protein
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {roundNumber(selectedFood.protein)}g
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Carbs
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {roundNumber(selectedFood.carbs)}g
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Fats
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {roundNumber(selectedFood.fats)}g
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Serving Size
                    </Typography>
                    <Slider
                      value={selectedFood.servingSize || 1}
                      min={1}
                      max={Math.max(selectedFood.servingSize * 2, 100)}
                      step={1}
                      valueLabelDisplay="auto"
                      sx={{
                        color: FIT_ORANGE,
                        '& .MuiSlider-thumb': {
                          backgroundColor: FIT_ORANGE,
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                        },
                      }}
                    />
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          {editMode ? (
            <>
              <Button 
                onClick={handleCloseModal}
                sx={{ color: theme.palette.text.primary }}
              >
                Cancel
              </Button>
              <ActionButton 
                startIcon={<Check size={18} />}
                variant="contained"
                color="primary"
                sx={{ bgcolor: FIT_ORANGE, color: '#fff' }}
                onClick={handleSaveEdit}
              >
                Save Changes
              </ActionButton>
            </>
          ) : (
            <>
              <ActionButton 
                startIcon={<Trash2 size={18} />}
                variant="contained"
                color="error"
                onClick={handleDeleteMeal}
              >
                Delete
              </ActionButton>
              <ActionButton 
                startIcon={<Edit2 size={18} />}
                variant="contained"
                color="primary"
                sx={{ bgcolor: FIT_ORANGE, color: '#fff' }}
                onClick={handleEditMode}
              >
                Edit
              </ActionButton>
            </>
          )}
        </DialogActions>
      </StyledDialog>
    </MacroCard>
  );
};

export default DetailedNutrition;